/**
 * Hook for managing ResponsiveCommandSelect event handlers
 * Consolidates all handler logic with proper memoization
 */

import React from "react";
import { addRecent } from "../recents";
import type { SelectOption } from "../types";

interface UseResponsiveSelectHandlersProps {
  options: SelectOption[];
  recentOptions: SelectOption[];
  onValueChange: (value: string, option?: SelectOption) => void;
  onSearchChange?: (val: string) => void;
  enableRecents: boolean;
  recentKey?: string;
  recentMax: number;
  onRecordRecent?: (item: { id: string; label: string }) => void;
  drawerId: string;
  closeDrawer: (id: string) => void;
  setOpen: (open: boolean) => void;
  setRecentsNonce: React.Dispatch<React.SetStateAction<number>>;
}

export function useResponsiveSelectHandlers({
  options,
  recentOptions,
  onValueChange,
  onSearchChange,
  enableRecents,
  recentKey,
  recentMax,
  onRecordRecent,
  drawerId,
  closeDrawer,
  setOpen,
  setRecentsNonce,
}: UseResponsiveSelectHandlersProps) {
  const handleSelect = React.useCallback(
    (id: string) => {
      const opt = options.find((o) => o.value === id);
      const selectedOpt = opt || recentOptions.find((o) => o.value === id);
      if (!selectedOpt) return;

      onValueChange(id, selectedOpt);

      if (enableRecents && recentKey) {
        const record = { id, label: selectedOpt.label };
        try {
          if (onRecordRecent) onRecordRecent(record);
          else addRecent(recentKey, record, recentMax);
        } catch {
          // no-op
        }
        setRecentsNonce((n: number) => n + 1);
      }

      setOpen(false);
      closeDrawer(drawerId);
      onSearchChange?.("");
    },
    [
      options,
      recentOptions,
      onValueChange,
      onSearchChange,
      enableRecents,
      recentKey,
      recentMax,
      onRecordRecent,
      drawerId,
      closeDrawer,
      setOpen,
      setRecentsNonce,
    ]
  );

  const handleClearRecents = React.useCallback(() => {
    setRecentsNonce((n: number) => n + 1);
  }, [setRecentsNonce]);

  return { handleSelect, handleClearRecents };
}
